import { addDays, format, isBefore, startOfDay } from "date-fns";

const TICKER_FORMAT = "yyyy-MM-dd";

const toTicker = (date: string | Date): string => format(startOfDay(new Date(date)), TICKER_FORMAT);

/**
 * The nights of the stay, one ticker (yyyy-MM-dd) per night from check-in up to (but not
 * including) check-out. Returns an empty array when the range is not valid.
 */
export function getStayNightTickers(checkIn: string | Date, checkOut: string | Date): string[] {
  const start = startOfDay(new Date(checkIn));
  const end = startOfDay(new Date(checkOut));
  const nights: string[] = [];
  for (let day = start; isBefore(day, end); day = addDays(day, 1)) {
    nights.push(format(day, TICKER_FORMAT));
  }
  return nights;
}

/**
 * Identifies the dates and inventory a set of daily values was computed for. The pickers update
 * before the rates finish loading, so the checks below only run once this matches the form.
 */
export function buildGridSyncKey({
  checkIn,
  checkOut,
  inventoryTicker,
}: {
  checkIn?: string;
  checkOut?: string;
  inventoryTicker?: string;
}): string {
  return [checkIn, checkOut, inventoryTicker].join("|");
}

/**
 * Stay nights a restricted user cannot price because no rate is loaded for them. Filtered against
 * the current stay so dates left over from a previous range are ignored.
 */
export function getUnratedStayDates({
  checkIn,
  checkOut,
  unratedDates,
}: {
  checkIn: string | Date;
  checkOut: string | Date;
  unratedDates: string[] | undefined;
}): Set<string> {
  if (!unratedDates?.length) {
    return new Set();
  }
  const stayNights = new Set(getStayNightTickers(checkIn, checkOut));
  return new Set(unratedDates.map(toTicker).filter(date => stayNights.has(date)));
}

/**
 * Dates that leave the daily values out of sync with the stay: dated outside
 * [check-in, check-out) — mirroring the backend validation — a stay night with no daily value,
 * or a stay night nobody priced yet, which is how the 0 the grid auto-fills on a date change
 * stays flagged. The save validation and the grid highlighting share this set, so a blocked
 * save always points at a flagged row.
 *
 * An amount of 0 is never rejected on its own (see ddb16133): a night counts as priced once it
 * was stored or the user typed into its row, whatever they entered. Skipped when the user
 * cannot edit prices, since they would have no way to clear it.
 */
export function getInvalidDailyValueDates({
  checkIn,
  checkOut,
  dailyValues,
  originalDailyValues,
  userPricedDates,
  canEditPrices = true,
}: {
  checkIn: string | Date;
  checkOut: string | Date;
  dailyValues: { ticker: string; amount?: number }[] | undefined;
  originalDailyValues?: { ticker: string }[];
  userPricedDates?: string[];
  canEditPrices?: boolean;
}): Set<string> {
  const invalidDates = new Set<string>();
  const stayNights = getStayNightTickers(checkIn, checkOut);
  if (stayNights.length === 0) {
    return invalidDates;
  }

  const stayNightSet = new Set(stayNights);
  const valueByDate = new Map(
    (dailyValues || []).map(dailyValue => [toTicker(dailyValue.ticker), dailyValue])
  );
  const pricedDates = new Set([
    ...(originalDailyValues || []).map(dailyValue => toTicker(dailyValue.ticker)),
    ...(userPricedDates || []).map(toTicker),
  ]);

  valueByDate.forEach((_, date) => {
    if (!stayNightSet.has(date)) {
      invalidDates.add(date);
    }
  });

  stayNights.forEach(night => {
    const dailyValue = valueByDate.get(night);
    if (!dailyValue) {
      invalidDates.add(night);
      return;
    }
    if (canEditPrices && !pricedDates.has(night) && !(Number(dailyValue.amount) > 0)) {
      invalidDates.add(night);
    }
  });

  return invalidDates;
}
